<script>
  import storefront from '@/lib/storefront.js';
  import { handleViewportSize, syncScroll } from '@/lib/vue-helpers.js';
  import * as queries from '@/lib/queries.js';
  import ProductListItem from '@/components/ProductListItem.vue';
  import { emitter } from '@/main.js';

  const GID_PREFIX = 'gid://shopify/Product/';
  const METAFIELD_KEY_REGEX = /^(custom|my_fields|plytix)\./;

  export default {
    name: 'ProductComparison',
    components: {
      ProductListItem
    },
    data() {
      return {
        error: '',
        loading: false,
        metafieldsLoading: false,
        metafieldsToCompare: '',
        colGap: null,
        rowGap: null,
        imageHeightDesktop: 320,
        imageHeightMobile: 180,
        bg: '#f0f0f0',
        colBg: '#ffffff',
        headerColor: '#000000',
        textOnBg: '#000000',
        backLinkFontSize: null,
        parsedCompareLines: [],
        compareDisplayByProduct: {},
        products: [],
        hideSimilar: false,
        rows: [],
        isSticky: false,
        enableSticky: true,
        originalOffsetTop: 0,
        urlParams: null,
        fetchRequestId: 0,
        cachedAspectRatio: null,
        productsReady: false,
        stickyTransitioning: false,
        emptyStateText: 'No products selected for comparison.'
      };
    },
    computed: {
      themeStyleVars() {
        return {
          '--pc-col-gap': `${Number(this.colGap)}px`,
          '--pc-row-gap': `${Number(this.rowGap)}px`,
          '--pc-image-height-desktop': `${Number(this.imageHeightDesktop)}px`,
          '--pc-image-height-mobile': `${Number(this.imageHeightMobile)}px`,
          '--pc-sticky-image-width': `${this.stickyImageWidth}px`,
          '--pc-sticky-image-width-mobile': `${this.stickyImageWidthMobile}px`,
          '--pc-bg': this.bg,
          '--pc-col-bg': this.colBg,
          '--pc-header-color': this.headerColor,
          '--pc-text-on-bg': this.textOnBg,
          '--pc-back-link-font-size': this.backLinkFontSize ? `${Number(this.backLinkFontSize)}px` : null,
          '--pc-shimmer-base': this.shimmerColors.base,
          '--pc-shimmer-sweep': this.shimmerColors.sweep,
          '--pc-shimmer-sweep-transparent': this.shimmerColors.sweepTransparent
        };
      },
      productsLength() {
        return this.products.length;
      },
      skeletonCount() {
        return this.productPids.length || 3;
      },
      aspectRatioParts() {
        const str = this.cachedAspectRatio || '3 / 4';
        const [w, h] = str.includes('/') ? str.split('/').map(s => parseFloat(s.trim())) : [parseFloat(str) || 0.75, 1];
        return { w, h };
      },
      cardWidth() {
        const imageHeight = Number(this.imageHeightDesktop);
        return Math.round(imageHeight * (this.aspectRatioParts.w / this.aspectRatioParts.h));
      },
      stickyImageWidth() {
        const stickyHeight = Math.round(Number(this.imageHeightDesktop) * 0.5);
        return Math.round(stickyHeight * (this.aspectRatioParts.w / this.aspectRatioParts.h));
      },
      stickyImageWidthMobile() {
        const mobileHeight = Number(this.imageHeightMobile);
        return Math.round(mobileHeight * (this.aspectRatioParts.w / this.aspectRatioParts.h));
      },
      skeletonGridStyle() {
        return {
          gridTemplateColumns: `repeat(${this.skeletonCount}, ${this.cardWidth}px)`
        };
      },
      expectedRowCount() {
        return this.parsedCompareLines.length || 4;
      },
      hasSimilarities() {
        return this.rows.some(({ values }) => {
          const trimmed = values.map(v => (v || '').trim());
          const nonEmpty = trimmed.filter(Boolean);
          return !(nonEmpty.length > 0 && (trimmed.includes('') || new Set(nonEmpty).size > 1));
        });
      },
      visibleRows() {
        if (!this.hideSimilar) return this.rows;
        return this.rows.filter(({ values }) => {
          const trimmed = values.map(v => (v || '').trim());
          const nonEmpty = trimmed.filter(Boolean);
          return nonEmpty.length > 0 && (trimmed.includes('') || new Set(nonEmpty).size > 1);
        });
      },
      productPids() {
        if (!this.urlParams) return [];
        try {
          const ids = [this.urlParams.get('products'), ...this.urlParams.getAll('product')]
            .filter(Boolean)
            .map(id => this.normalizeProductId(id))
            .filter(Boolean);
          return Array.from(new Set(ids)).slice(0, 3);
        } catch (e) {
          console.error('Error getting product pids', e);
          return [];
        }
      },
      collectionHandle() {
        return this.urlParams?.get('collection_handle') || '';
      },
      collectionTitle() {
        return this.urlParams?.get('collection_title') || '';
      },
      safeCollectionUrl() {
        if (!this.collectionHandle) return null;
        const sanitized = this.sanitizeCollectionHandle(this.collectionHandle);
        return sanitized ? `/collections/${sanitized}` : null;
      },
      compareGridStyle() {
        return {
          gridTemplateColumns: `repeat(${this.productsLength}, ${this.cardWidth}px)`
        };
      },
      shimmerColors() {
        const hex = (this.colBg || '#ffffff').replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16) || 0;
        const g = parseInt(hex.substring(2, 4), 16) || 0;
        const b = parseInt(hex.substring(4, 6), 16) || 0;
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        const isLight = luminance > 0.5;
        const offset = isLight ? -20 : 20;
        const clamp = v => Math.max(0, Math.min(255, v));
        const baseR = clamp(r + offset);
        const baseG = clamp(g + offset);
        const baseB = clamp(b + offset);
        return {
          base: `rgb(${baseR}, ${baseG}, ${baseB})`,
          sweep: this.colBg || '#ffffff',
          sweepTransparent: `rgba(${r}, ${g}, ${b}, 0)`
        };
      },
      hasNoProducts() {
        return this.productsReady && !this.products.length && !this.loading && !this.error;
      }
    },
    watch: {
      visibleRows() {
        this.$nextTick(() => {
          this.cleanupScrollSync?.();
          this.setupScrollSync();
        });
      },
      isSticky() {
        if (!this.enableSticky) return;
        this.$nextTick(() => {
          this.cleanupScrollSync?.();
          this.cleanupScrollSync = null;
          requestAnimationFrame(() => {
            this.setupScrollSync();
          });
        });
      },
      stickyImageWidthMobile() {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.cleanupScrollSync?.();
            this.setupScrollSync();
          });
        });
      }
    },
    mounted() {
      this.handleViewportSize = handleViewportSize(this);
      const arStr = getComputedStyle(document.documentElement).getPropertyValue('--PRODUCT-GRID-ASPECT-RATIO-DECIMAL').trim();
      if (arStr) this.cachedAspectRatio = arStr;
      try {
        this.urlParams = new URLSearchParams(window.location.search || '');
      } catch (e) {
        console.error('Error initializing URL params', e);
        this.urlParams = new URLSearchParams();
      }
      this.metafieldsToCompare = this.$el?.parentElement?.dataset?.metafieldsToCompare;
      this.colGap = Number(this.$el?.parentElement?.dataset?.colGap) || this.colGap;
      this.rowGap = Number(this.$el?.parentElement?.dataset?.rowGap) || this.rowGap;
      this.imageHeightDesktop = Number(this.$el?.parentElement?.dataset?.imageHeightDesktop) || this.imageHeightDesktop;
      this.imageHeightMobile = Number(this.$el?.parentElement?.dataset?.imageHeightMobile) || this.imageHeightMobile;
      this.bg = this.$el?.parentElement?.dataset?.bg || this.bg;
      this.colBg = this.$el?.parentElement?.dataset?.colBg || this.colBg;
      this.headerColor = this.$el?.parentElement?.dataset?.headerColor || this.headerColor;
      this.textOnBg = this.$el?.parentElement?.dataset?.textOnBg || this.textOnBg;
      this.backLinkFontSize = this.$el?.parentElement?.dataset?.backLinkFontSize ? Number(this.$el?.parentElement?.dataset?.backLinkFontSize) : null;
      this.emptyStateText = this.$el?.parentElement?.dataset?.emptyStateText || this.emptyStateText;
      const enableStickyAttr = this.$el?.parentElement?.dataset?.enableSticky;
      this.enableSticky = enableStickyAttr !== undefined ? enableStickyAttr === 'true' : true;
      this.handlePopstate = () => {
        try {
          this.urlParams = new URLSearchParams(window.location.search);
        } catch (e) {
          console.error('Error updating URL params', e);
          this.urlParams = new URLSearchParams();
        }
        this.fetchProducts();
      };
      this._imageLoadCount = 0;
      this._onProductImageLoaded = () => {
        this._imageLoadCount++;
        if (this._imageLoadCount >= this.products.length && this.products.length > 0 && !this.productsReady) {
          this.productsReady = true;
          clearTimeout(this._readyTimeout);
        }
      };
      emitter.on('product-slide-loaded', this._onProductImageLoaded);
      window.addEventListener('popstate', this.handlePopstate);
      this.fetchProducts();
      this.$nextTick(() => {
        if (this.enableSticky) {
          this.setupStickyScroll();
        }
        this.setupScrollSync();
      });
    },
    beforeUnmount() {
      this.handleViewportSize?.();
      if (this.enableSticky) {
        this.cleanupStickyScroll();
      }
      this.cleanupScrollSync?.();
      if (this.handlePopstate) {
        window.removeEventListener('popstate', this.handlePopstate);
        this.handlePopstate = null;
      }
      if (this._onProductImageLoaded) {
        emitter.off('product-slide-loaded', this._onProductImageLoaded);
      }
      clearTimeout(this._readyTimeout);
      clearTimeout(this._stickyTransitionTimer);
    },
    methods: {
      sanitizeCollectionHandle(handle) {
        if (!handle || typeof handle !== 'string') return null;
        const sanitized = handle.replace(/[^a-zA-Z0-9_-]/g, '');
        return sanitized.length > 0 && sanitized === handle ? sanitized : null;
      },
      normalizeProductId(productId) {
        if (!productId || typeof productId !== 'string') return null;
        return productId.startsWith(GID_PREFIX) ? productId.replace(GID_PREFIX, '') : (/^\d+$/.test(productId) ? productId : null);
      },
      getNormalizedProductId(productId) {
        return this.normalizeProductId(productId) || '';
      },
      setupStickyScroll() {
        if (!this.enableSticky) return;
        const stickyContainer = this.$el?.querySelector('.product-comparison__sticky-container');
        if (!stickyContainer) return;

        this.originalOffsetTop = stickyContainer.offsetTop;
        this.handleScroll = e => {
          if (!this.enableSticky) {
            this.isSticky = false;
            return;
          }
          const scrollY = e?.detail?.position ?? window.scrollY ?? window.pageYOffset;
          const shouldStick = scrollY > this.originalOffsetTop;
          if (shouldStick !== this.isSticky) {
            this.stickyTransitioning = true;
            clearTimeout(this._stickyTransitionTimer);
            this._stickyTransitionTimer = setTimeout(() => { this.stickyTransitioning = false; }, 80);
          }
          this.isSticky = shouldStick;
        };

        document.addEventListener('theme:scroll', this.handleScroll);
        this.handleScroll({ detail: { position: window.scrollY ?? window.pageYOffset } });
      },
      cleanupStickyScroll() {
        if (this.handleScroll) {
          document.removeEventListener('theme:scroll', this.handleScroll);
          this.handleScroll = null;
        }
      },
      setupScrollSync() {
        this.$nextTick(() => {
          const container = this.$el?.querySelector('.product-comparison__content');
          if (!container) return;
          this.cleanupScrollSync = syncScroll(
            container,
            '.product-comparison__products, .product-comparison__compare-metafields, .product-comparison__compare-values',
            'horizontal'
          );
        });
      },
      removeProduct(productId) {
        const removedPid = this.normalizeProductId(productId);
        this.products = this.products.filter(p => p.id !== productId);
        const pids = this.products.map(p => this.normalizeProductId(p.id)).filter(Boolean);
        const params = new URLSearchParams();
        const ch = this.collectionHandle;
        const ct = this.collectionTitle;
        if (ch) params.set('collection_handle', ch);
        if (ct) params.set('collection_title', ct);
        if (pids.length) {
          params.set('products', pids[0]);
          for (let i = 1; i < pids.length; i++) params.append('product', pids[i]);
        }
        const url = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
        if (url !== window.location.pathname + window.location.search) {
          window.history.pushState({}, '', url);
          this.urlParams = params;
        }
        try {
          const compareKey = `plp_compare_products_${window.location.hostname}`;
          const stored = sessionStorage.getItem(compareKey);
          if (stored) {
            const arr = JSON.parse(stored);
            if (Array.isArray(arr)) {
              const filtered = arr.filter(p => {
                const pId = String(p?.pid || p?.id || '');
                return pId !== removedPid && pId !== String(productId);
              });
              if (filtered.length) {
                sessionStorage.setItem(compareKey, JSON.stringify(filtered));
              } else {
                sessionStorage.removeItem(compareKey);
              }
            }
          }
        } catch (e) {
          console.error('Error updating sessionStorage:', e);
        }
        this.rebuildRows();
        this.$nextTick(() => {
          this.cleanupScrollSync?.();
          this.setupScrollSync();
        });
      },
      extractRowLabel(left, right) {
        if (!this.isMetafieldKey(left)) return String(left || '').trim();
        if (!this.isMetafieldKey(right)) return String(right || '').trim();
        return '';
      },
      rebuildRows() {
        if (!this.parsedCompareLines.length) {
          this.rows = [];
          return;
        }
        this.rows = this.parsedCompareLines
          .map((line, idx) => {
            const [left, right] = line || [];
            const label = this.extractRowLabel(left, right);
            const values = this.products.map(p => {
              const row = this.compareDisplayByProduct?.[p.id]?.[idx];
              return Array.isArray(row) && typeof row[1] === 'string' ? row[1].trim() : '';
            });
            return { label, values };
          })
          .filter(row => row.values.some(v => v.trim() !== ''));
      },
      isMetafieldKey(val) {
        return METAFIELD_KEY_REGEX.test(String(val || '').trim());
      },
      parseMetafieldKey(name) {
        if (!name) return null;
        const [namespace, key] = String(name).trim().split('.');
        return namespace && key ? { namespace: namespace.trim(), key: key.trim() } : null;
      },
      parseMetafieldsSetting(rawSetting) {
        if (!rawSetting) return { identifiers: [], lines: [] };
        const idMap = new Map();
        const lines = rawSetting
          .split(/\r?\n/)
          .map(row => row?.trim())
          .filter(Boolean)
          .map(row => {
            const [left = '', right = ''] = row.split(',').map(c => (c || '').trim());
            [left, right].forEach(token => {
              if (this.isMetafieldKey(token)) {
                const parsed = this.parseMetafieldKey(token);
                if (parsed) idMap.set(`${parsed.namespace}.${parsed.key}`, parsed);
              }
            });
            return [left, right];
          });
        return { identifiers: Array.from(idMap.values()), lines };
      },
      formatCompareValue(raw) {
        if (!raw || typeof raw !== 'string') return '';
        const trimmed = raw.trim();
        if (!trimmed) return '';

        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) return parsed.map(v => String(v ?? '').trim()).filter(Boolean).join(', ');
            if (parsed && typeof parsed === 'object') {
              if (parsed.type === 'root' && Array.isArray(parsed.children)) return this.extractRichText(parsed);
              if ('amount' in parsed) return [parsed.amount, parsed.currency_code].filter(Boolean).join(' ');
              if ('value' in parsed && 'unit' in parsed) return `${parsed.value} ${parsed.unit}`;
              if ('value' in parsed && 'scale_max' in parsed) return `${parsed.value}/${parsed.scale_max}`;
              if ('text' in parsed && 'url' in parsed) return String(parsed.text || parsed.url || '');
              return Object.values(parsed).filter(v => v != null && v !== '').join(', ');
            }
          } catch (e) {
            // Not valid JSON — fall through to HTML/text handling
          }
        }

        if (/<[a-z][\s\S]*>/i.test(trimmed)) return this.stripHtmlToText(trimmed);

        if (trimmed.includes('\n')) return trimmed.split('\n').map(s => s.trim()).filter(Boolean).join(' | ');

        return trimmed;
      },
      extractRichText(node) {
        if (!node) return '';
        if (node.type === 'text') return node.value || '';
        if (!Array.isArray(node.children)) return '';
        const blockTypes = new Set(['paragraph', 'heading', 'list-item', 'list']);
        const parts = node.children.map(child => {
          if (child.type === 'list') {
            return (child.children || []).map(li => this.extractRichText(li).trim()).filter(Boolean).join(' | ');
          }
          return this.extractRichText(child);
        });
        const sep = blockTypes.has(node.type) || node.type === 'root' ? ' | ' : '';
        return parts.filter(Boolean).join(sep).replace(/\s{2,}/g, ' ').trim();
      },
      stripHtmlToText(html) {
        const el = document.createElement('div');
        el.innerHTML = html;
        const listItems = el.querySelectorAll('li');
        if (listItems.length) return Array.from(listItems).map(li => li.textContent.trim()).filter(Boolean).join(' | ');
        return (el.textContent || '').replace(/\s{2,}/g, ' ').trim();
      },
      buildDisplayLines(lines, metafieldsArray) {
        const byKey = Array.isArray(metafieldsArray)
          ? metafieldsArray.reduce((acc, mf) => {
            if (mf?.namespace && mf?.key) acc[`${mf.namespace}.${mf.key}`] = mf.value ?? '';
            return acc;
          }, {})
          : {};
        const valueForKeyToken = token => {
          if (!this.isMetafieldKey(token)) return '';
          const parsed = this.parseMetafieldKey(token);
          return parsed ? byKey[`${parsed.namespace}.${parsed.key}`] || '' : '';
        };
        return lines.map(([left, right]) => {
          const lIsKey = this.isMetafieldKey(left);
          const rIsKey = this.isMetafieldKey(right);
          const label = !lIsKey ? String(left || '').trim() : (!rIsKey ? String(right || '').trim() : '');
          const valueToken = lIsKey ? left : (rIsKey ? right : '');
          return [label, this.formatCompareValue(valueForKeyToken(valueToken))];
        });
      },
      async fetchProductMetafields(product, identifiers, countryIso2, requestId) {
        try {
          const handle = product?.handle;
          if (!handle) {
            console.warn('Product missing handle:', product?.id);
            return;
          }
          const mfResp = await storefront.request(queries.metafieldsByProductHandle, {
            variables: { handle, countryIso2, metaIdentifier: identifiers }
          });
          if (requestId !== this.fetchRequestId) return;

          const metafields = mfResp?.data?.product?.metafields || [];
          const displayLines = this.buildDisplayLines(this.parsedCompareLines, metafields);
          if (displayLines.length && displayLines.every(([, val]) => !val)) {
            console.warn(`[ProductComparison] No metafield values found for product: ${handle} — check that configured keys exist on this product`);
          }
          this.compareDisplayByProduct = { ...this.compareDisplayByProduct, [product.id]: displayLines };
        } catch (err) {
          console.error(`Failed to fetch metafields for product ${product?.id}:`, err);
          if (requestId === this.fetchRequestId) {
            this.compareDisplayByProduct = { ...this.compareDisplayByProduct, [product.id]: [] };
          }
        }
      },
      async fetchProducts() {
        if (!this.productPids.length) {
          this.productsReady = true;
          return;
        }
        const currentRequestId = ++this.fetchRequestId;
        this.loading = true;
        this.productsReady = false;
        this._imageLoadCount = 0;
        clearTimeout(this._readyTimeout);
        this.error = '';
        try {
          const { identifiers, lines } = this.parseMetafieldsSetting(this.metafieldsToCompare);
          this.parsedCompareLines = lines;

          if (!identifiers.length) {
            console.warn('[ProductComparison] No metafields configured for comparison. Add metafield keys to the "Compare Metafields" section setting.');
          }

          const query = this.productPids.map(id => `id:${id}`).join(' OR ');
          const countryIso2 = window?.Shopify?.country;
          const response = await storefront.request(queries.productMasterByIds, {
            variables: { query, countryIso2 }
          });

          if (currentRequestId !== this.fetchRequestId) return;

          const edges = response?.data?.products?.edges || [];
          const nodesById = new Map(
            edges
              .map(({ node }) => {
                const normalizedId = this.normalizeProductId(node?.id);
                return normalizedId ? [normalizedId, node] : null;
              })
              .filter(Boolean)
          );
          this.products = this.productPids.map(pid => nodesById.get(String(pid))).filter(Boolean);

          if (identifiers.length && this.products.length) {
            this.metafieldsLoading = true;
            const country = window?.Shopify?.country;
            try {
              await Promise.all(
                this.products.map(prod => this.fetchProductMetafields(prod, identifiers, country, currentRequestId))
              );
            } finally {
              if (currentRequestId === this.fetchRequestId) this.metafieldsLoading = false;
            }
            if (currentRequestId === this.fetchRequestId) {
              this.rebuildRows();
              if (!this.rows.length && this.parsedCompareLines.length) {
                console.warn('[ProductComparison] All comparison rows are empty. Configured keys:', identifiers.map(i => `${i.namespace}.${i.key}`));
              }
            }
          }
          if (currentRequestId === this.fetchRequestId && this.products.length) {
            this.$nextTick(() => {
              this.cleanupScrollSync?.();
              this.setupScrollSync();
            });
          }
        } catch (err) {
          if (currentRequestId === this.fetchRequestId) {
            this.error = err?.message || 'Failed to load products.';
            this.productsReady = true;
            console.error('Error fetching products:', err);
          }
        } finally {
          if (currentRequestId === this.fetchRequestId) {
            this.loading = false;
            if (!this.products.length) {
              this.productsReady = true;
            } else {
              this._readyTimeout = setTimeout(() => {
                if (!this.productsReady) this.productsReady = true;
              }, 5000);
            }
          }
        }
      }
    }
  };
</script>

<template>
  <section class="product-comparison section-padding wrapper--full" :style="themeStyleVars">
    <a v-if="safeCollectionUrl && collectionTitle" class="gt-p product-comparison__back-link"
      :href="safeCollectionUrl">
      &lt; Back to {{ collectionTitle }} </a>
        <div class="product-comparison__content">
          <div class="product-comparison__sticky-container" :class="{ 'is-sticky': enableSticky && isSticky }">
            <h1 class="product-comparison__title gt-h2">Compare Products</h1>
            <div class="product-comparison__products-section" :class="{ 'product-comparison__products-section--transitioning': stickyTransitioning }">
              <div v-if="error" class="product-comparison__error gt-p">{{ error }}</div>
              <div v-else-if="hasNoProducts" class="product-comparison__empty-state gt-p">
                <p>{{ emptyStateText }}</p>
                <a v-if="safeCollectionUrl && collectionTitle" :href="safeCollectionUrl" class="product-comparison__back-link gt-p">&lt; Back to {{ collectionTitle }}</a>
              </div>
              <div v-else class="product-comparison__products-stack">
                <div class="product-comparison__skeleton-grid" :class="{ 'product-comparison__skeleton--hidden': productsReady }" :style="skeletonGridStyle">
                  <div v-for="n in skeletonCount" :key="'skeleton-' + n" class="product-comparison__item product-comparison__skeleton-card">
                    <div class="product-comparison__skeleton-image"></div>
                    <div class="product-comparison__skeleton-info">
                      <div class="product-comparison__skeleton-title"></div>
                      <div class="product-comparison__skeleton-price"></div>
                      <div class="product-comparison__skeleton-link"></div>
                    </div>
                  </div>
                </div>
                <div v-if="products.length" class="product-comparison__products" :class="{ 'product-comparison__products--ready': productsReady }" :style="compareGridStyle">
                  <div v-for="product in products" :key="product.id" class="product-comparison__item">
                    <button v-if="products.length > 2" type="button" class="product-comparison__remove"
                      aria-label="Remove product from comparison" @click="removeProduct(product.id)">X</button>
                    <ProductListItem v-if="product?.title" :productTitle="product?.title"
                      :pid="getNormalizedProductId(product?.id)" productImageName="product-grid"
                      :key="'grid' + product.id" :hideSwatches="true" :disableCompare="true" :disableSlider="true"
                      showSalePercentage="false" :showFullDetailsLink="true" :showRating="true" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="products.length" class="product-comparison__compare-metafields">
            <div class="product-comparison__compare-head" :style="compareGridStyle">
              <div v-if="hasSimilarities" class="product-comparison__compare-controls">
                <input id="hide-similar-checkbox" type="checkbox" v-model="hideSimilar" />
                <label for="hide-similar-checkbox" class="gt-p__small">Hide similarities</label>
              </div>
            </div>
            <div class="product-comparison__rows-stack">
              <div class="product-comparison__compare-rows product-comparison__skeleton-rows" :class="{ 'product-comparison__skeleton-rows--hidden': !metafieldsLoading }">
                <div v-for="n in expectedRowCount" :key="'skel-row-' + n" class="product-comparison__compare-row">
                  <div class="product-comparison__skeleton-row-label"></div>
                  <div class="product-comparison__compare-values" :style="skeletonGridStyle">
                    <div v-for="c in skeletonCount" :key="'skel-val-' + n + '-' + c" class="product-comparison__skeleton-row-value"></div>
                  </div>
                </div>
              </div>
              <div v-if="rows.length" class="product-comparison__compare-rows product-comparison__compare-rows--real" :class="{ 'product-comparison__compare-rows--visible': !metafieldsLoading }">
                <div class="product-comparison__compare-row" v-for="(row, idx) in visibleRows" :key="'row-' + idx">
                  <div class="product-comparison__compare-label gt-p__bold">{{ row.label.toUpperCase() }}</div>
                  <div class="product-comparison__compare-values" :style="compareGridStyle">
                    <div class="product-comparison__compare-value gt-p__small" v-for="(val, vIdx) in row.values" :key="'val-' + vIdx">{{ val || '—' }}</div>
                  </div>
                </div>
              </div>
              <div v-else-if="!loading && !metafieldsLoading && products.length" class="product-comparison__no-fields gt-p">
                No comparable fields found for the selected products.
              </div>
            </div>
          </div>
        </div>
  </section>
</template>

<style lang="scss">
  .product-comparison {
    background-color: var(--pc-bg, hsla(0, 0%, 94%, 1));

    [data-horizontal-scroll] {
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .product-comparison__content {
      position: relative;
      max-width: 1200px;
      margin: 0 auto;
      width: 100%;
    }

    .product-comparison__sticky-container {
      position: relative;
      transition: background-color 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding-top 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding-bottom 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      background-color: transparent;

      &.is-sticky {
        position: sticky;
        top: 60px;
        z-index: 100;
        padding-top: 12px;
        background-color: var(--pc-bg, hsla(0, 0%, 94%, 1));

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: calc(-50vw + 50%);
          right: calc(-50vw + 50%);
          bottom: 0;
          background-color: var(--pc-bg, hsla(0, 0%, 94%, 1));
          z-index: -1;
          transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          opacity: 1;
        }
      }

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: calc(-50vw + 50%);
        right: calc(-50vw + 50%);
        bottom: 0;
        background-color: var(--pc-bg, hsla(0, 0%, 94%, 1));
        z-index: -1;
        transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        opacity: 0;
        pointer-events: none;
      }
    }

    .product-comparison__title {
      text-align: center;
      color: var(--pc-header-color, inherit);
      margin-bottom: 24px;
    }

    .product-comparison__products-section {
      position: relative;
      transition: opacity 0.15s ease;
    }

    .product-comparison__products-section--transitioning {
      opacity: 0;
    }

    .product-comparison__products-stack {
      display: grid;
      grid-template-columns: minmax(0, 1fr);

      & > * {
        grid-area: 1 / 1;
      }
    }

    .product-comparison__products {
      display: grid;
      justify-content: center;
      gap: var(--pc-col-gap, 12px);
      overflow-x: auto;
      overflow-y: visible;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .product-comparison__sticky-container.is-sticky .product-comparison__products {
      overflow-x: auto;
      overflow-y: visible;
      touch-action: pan-x pan-y;
      width: fit-content;
      margin: 0 auto;
      box-shadow: -15px 3px 15px -12px rgba(0, 0, 0, 0.4), 15px 3px 15px -12px rgba(0, 0, 0, 0.4), 0 3px 15px 0px rgba(0, 0, 0, 0.4);
      transition: box-shadow 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      clip-path: inset(0px -15px -15px -15px);
    }

    .product-comparison__item {
      text-align: center;
      position: relative;
      background-color: var(--pc-col-bg, #fff);
    }

    .product-comparison__sticky-container.is-sticky .product-comparison__item {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      text-align: left;
      aspect-ratio: initial;
    }

    .product-comparison__item .product-grid-item {
      display: flex;
      flex-direction: column;
    }

    .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-item {
      aspect-ratio: initial;
      flex-direction: row;
      align-items: stretch;
    }

    .product-comparison__item .product-grid-item__container {
      display: block;
      width: 100%;
      transition: width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), min-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }

    .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-item__container {
      flex: 0 0 auto;
      width: var(--pc-sticky-image-width);
      min-width: var(--pc-sticky-image-width);
      display: flex;
      flex-direction: column;
      align-self: stretch;
      background-color: var(--pc-col-bg, #fff);
    }

    .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-item__container>a {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      height: 100%;
    }


    .product-comparison__item .product-grid-carousel-wrap,
    .product-comparison__item .product-grid-single-wrap {
      height: var(--pc-image-height-desktop);
      width: 100%;
      overflow: hidden;
      position: relative;
      aspect-ratio: var(--PRODUCT-GRID-ASPECT-RATIO-DECIMAL);
      transition: height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }

    .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-carousel-wrap,
    .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-single-wrap {
      height: auto;
      width: var(--pc-sticky-image-width);
      flex: 1 1 auto;
      min-height: calc(var(--pc-image-height-desktop) * 0.5);
    }

    .product-comparison__item .product-grid-carousel-wrap .carousel,
    .product-comparison__item .product-grid-carousel-wrap .carousel__viewport,
    .product-comparison__item .product-grid-carousel-wrap .carousel__track {
      height: 100%;
    }

    .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-carousel-wrap .carousel,
    .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-carousel-wrap .carousel__viewport,
    .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-carousel-wrap .carousel__track {
      height: 100%;
    }

    .product-comparison__item .product-grid-item__image {
      width: 100%;
      height: 100%;
      object-fit: var(--PRODUCT-GRID-OBJECT-FIT);
      aspect-ratio: auto !important;
      background: var(--pc-col-bg, #fff);
    }

    .product-comparison__item .product-grid-carousel-wrap,
    .product-comparison__item .product-grid-single-wrap {
      background-color: var(--pc-col-bg, #fff);
    }

    .product-comparison__item .product-grid-single-wrap img {
      width: 100%;
      height: 100%;
      display: block;
      margin: 0 auto;
      object-fit: var(--PRODUCT-GRID-OBJECT-FIT);
      aspect-ratio: auto !important;
    }

    .product-comparison__item .product__grid__info {
      position: relative;
      transition: min-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding-left 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding-right 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }

    .product-comparison__sticky-container.is-sticky .product-comparison__item .product__grid__info {
      flex: 1 1 auto;
      position: relative;
      min-width: 0;
    }

    .product-comparison__item .product__grid__info>a[data-grid-link] {
      display: block;
      width: 100%;
      margin: 0;
      background-color: var(--pc-col-bg, #fff);
      padding: 8px;
    }

    .product-comparison__item,
    .product-comparison__item a,
    .product-comparison__item .product__grid__info,
    .product-comparison__item .product__grid__title,
    .product-comparison__item .product__grid__price,
    .product-comparison__item .view-product-details {
      color: var(--pc-text-on-bg, inherit);
    }

    .product-comparison__item .product__grid__price,
    .product-comparison__item .price,
    .product-comparison__item .compare-at,
    .product-comparison__item .view-product-details {
      background-color: var(--pc-col-bg, #fff);
    }

    .product-comparison__empty-state {
      text-align: center;
      padding: 2em 1em;
      color: var(--pc-header-color, inherit);
    }

    .product-comparison__no-fields {
      text-align: center;
      padding: 2em 1em;
      color: var(--pc-header-color, inherit);
      opacity: 0.7;
    }

    .product-comparison__compare-metafields {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      justify-content: center;
      justify-items: center;
      margin: 24px 0;
    }

    .product-comparison__compare-rows {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: var(--pc-row-gap);
      width: 100%;
    }

    .product-comparison__compare-head {
      display: grid;
      justify-content: center;
      gap: var(--pc-col-gap);
      margin-bottom: 12px;
      overflow: hidden;
    }

    .product-comparison__compare-controls {
      display: flex;
      align-items: center;
      gap: 8px;

      label {
        margin-left: 0;
        color: var(--pc-header-color, inherit);
        vertical-align: middle;
      }

      input[type='checkbox'] {
        appearance: none;
        box-sizing: border-box;
        width: 18px;
        height: 18px;
        border: 1px solid #000;
        border-radius: 3px;
        background-color: #fff;
        display: inline-block;
        position: relative;
        cursor: pointer;
        margin: 0;
      }

      input[type='checkbox']:checked::after {
        content: '';
        position: absolute;
        inset: 0;
        background-repeat: no-repeat;
        background-position: center;
        background-size: 80% 80%;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 8l3 3 7-7' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      }
    }

    .product-comparison__compare-row {
      max-width: fit-content;
      margin: 0 auto;
    }

    .product-comparison__compare-label {
      width: 100%;
      margin: 0.5em 0;
      color: var(--pc-header-color, inherit);
    }

    .product-comparison__compare-values {
      display: grid;
      justify-content: center;
      align-items: stretch;
      gap: var(--pc-col-gap, 12px);
    }

    .product-comparison__compare-value {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--pc-text-on-bg, inherit);
      text-align: center;
      background-color: var(--pc-col-bg, #fff);
      padding: 1em;
    }

    .is-mobile & {
      .product-comparison__compare-head {
        display: flex;
        gap: var(--pc-col-gap, 12px);
        justify-content: flex-start;
      }

      .product-comparison__compare-controls {
        flex: 0 0 auto;
        min-width: 350px;
      }

      .product-comparison__products {
        display: flex;
        overflow-x: auto;
        gap: var(--pc-col-gap, 12px);
        justify-content: flex-start;
        margin: 0 var(--outer-offset);
        width: calc(100% + var(--outer) * 2);
        padding-left: var(--outer);
        padding-right: var(--outer);
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      .product-comparison__item {
        flex: 1 1 0;
        min-width: var(--pc-sticky-image-width-mobile);
        max-width: 240px;
      }

      .product-comparison__item .product-grid-item__container {
        width: 100%;
      }

      .product-comparison__item .product-grid-carousel-wrap,
      .product-comparison__item .product-grid-single-wrap {
        width: 100%;
        height: auto;
        aspect-ratio: var(--PRODUCT-GRID-ASPECT-RATIO-DECIMAL);
      }

      .product-comparison__item .product-grid-item__image {
        width: 100%;
        height: 100%;
      }

      .product-comparison__item .product-grid-single-wrap img {
        width: 100%;
        height: 100%;
      }

      .product-comparison__compare-row {
        max-width: none;
        margin: 0;
      }

      .product-comparison__compare-values {
        display: flex;
        overflow-x: auto;
        gap: var(--pc-col-gap, 12px);
        justify-content: flex-start;
        margin: 0 var(--outer-offset);
        width: calc(100% + var(--outer) * 2);
        padding-left: var(--outer);
        padding-right: var(--outer);
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      .product-comparison__compare-value {
        flex: 1 1 0;
        min-width: var(--pc-sticky-image-width-mobile);
        max-width: 240px;
      }

      .product-comparison__compare-label {
        margin-left: var(--outer-offset);
        margin-right: var(--outer-offset);
        padding-left: var(--outer);
        padding-right: var(--outer);
        width: calc(100% + var(--outer) * 2);
      }

      .product__grid__info .product__grid__title {
        margin: 0;
      }

      .product-comparison__sticky-container.is-sticky .product-comparison__products {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: var(--pc-col-gap, 12px);
        justify-content: flex-start;
        margin: 0 var(--outer-offset);
        width: calc(100% + var(--outer) * 2);
        padding-left: var(--outer);
        padding-right: var(--outer);
        touch-action: pan-x pan-y;
        overscroll-behavior-x: none;
        scrollbar-width: none;
        -ms-overflow-style: none;
        box-shadow: none;
        clip-path: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      .product-comparison__sticky-container.is-sticky .product-comparison__item {
        scroll-snap-align: start;
        display: block;
        text-align: center;
      }

      .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-item {
        flex-direction: column;
      }

      .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-item__container {
        width: 100%;
      }

      .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-carousel-wrap,
      .product-comparison__sticky-container.is-sticky .product-comparison__item .product-grid-single-wrap {
        width: 100%;
        height: auto;
        aspect-ratio: var(--PRODUCT-GRID-ASPECT-RATIO-DECIMAL);
      }

      .product-comparison__sticky-container.is-sticky .product-comparison__item .product__grid__info {
        padding-left: 0;
        padding-right: 0;

        [data-grid-link]>*:not(.product__grid__title__wrapper) {
          display: none;
        }
      }

      .product-comparison__remove {
        top: 0;
        right: 0;
      }
    }


    .product-comparison__remove {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 18px;
      height: 18px;
      border: none;
      cursor: pointer;
      z-index: 1;
      background-color: transparent;
      color: #000;
    }

    .product-comparison__sticky-container.is-sticky .product-comparison__remove {
      position: absolute;
      top: 0;
      right: 0;
      z-index: 2;
    }

    .product-comparison__sticky-container.is-sticky .product-comparison__item .product__grid__info {
      flex: 1 1 auto;
      position: relative;
      min-width: 0;
    }

    .product-comparison__back-link {
      display: block;
      padding-top: 12px;
      color: var(--pc-header-color, inherit);
      font-size: var(--pc-back-link-font-size);
    }

    .product-comparison__sticky-container.is-sticky .topLeftBadge {
      display: none;
    }

    .is-mobile & .topLeftBadge {
      display: none;
    }

    .product__grid__title__wrapper.promo-badge {
      display: none;
    }

    .pillbadges {
      display: none;
    }

    @keyframes pcShimmer {
      0% { transform: translateX(-100%) rotate(32deg); }
      100% { transform: translateX(100%) rotate(8deg); }
    }

    .product-comparison__skeleton-grid {
      display: grid;
      justify-content: center;
      gap: var(--pc-col-gap, 12px);
      z-index: 1;
      transition: opacity 0.35s ease;
      overflow: hidden;
    }

    .product-comparison__skeleton--hidden {
      opacity: 0;
      pointer-events: none;
      visibility: hidden;
      height: 0;
      overflow: hidden;
    }

    .product-comparison__products-stack > .product-comparison__products {
      opacity: 0;
      transition: opacity 0.35s ease;

      &.product-comparison__products--ready {
        opacity: 1;
      }
    }

    .product-comparison__skeleton-card {
      background-color: var(--pc-col-bg, #fff);
    }

    .product-comparison__skeleton-image,
    .product-comparison__skeleton-title,
    .product-comparison__skeleton-price,
    .product-comparison__skeleton-link,
    .product-comparison__skeleton-row-label,
    .product-comparison__skeleton-row-value {
      position: relative;
      background-color: var(--pc-shimmer-base, #f6f6f6);
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        inset: -30%;
        width: 200%;
        height: 200%;
        background: linear-gradient(to right, var(--pc-shimmer-sweep-transparent, rgba(255,255,255,0)) 16%, var(--pc-shimmer-sweep, #fff) 32%, var(--pc-shimmer-sweep-transparent, rgba(255,255,255,0)) 48%);
        animation: pcShimmer 2.4s linear infinite;
        transform-origin: center;
        pointer-events: none;
        transform: translateX(-100%) rotate(32deg);
        z-index: 1;
      }
    }

    .product-comparison__skeleton-image {
      width: 100%;
      aspect-ratio: var(--PRODUCT-GRID-ASPECT-RATIO-DECIMAL, 3 / 4);
    }

    .product-comparison__skeleton-info {
      padding: 12px 8px;
    }

    .product-comparison__skeleton-title {
      height: 18px;
      width: 80%;
      margin-bottom: 8px;
    }

    .product-comparison__skeleton-price {
      height: 14px;
      width: 40%;
    }

    .product-comparison__skeleton-link {
      height: 14px;
      width: 55%;
      margin-top: 8px;
    }

    .product-comparison__skeleton-row-label {
      height: 16px;
      width: 120px;
      margin: 0.5em 0;
    }

    .product-comparison__skeleton-row-value {
      height: 40px;
    }

    .product-comparison__rows-stack {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      width: 100%;

      & > * {
        grid-area: 1 / 1;
      }
    }

    .product-comparison__skeleton-rows {
      z-index: 1;
      transition: opacity 0.35s ease;
    }

    .product-comparison__skeleton-rows--hidden {
      opacity: 0;
      pointer-events: none;
      visibility: hidden;
      height: 0;
      overflow: hidden;
    }

    .product-comparison__compare-rows--real {
      opacity: 0;
      transition: opacity 0.35s ease;

      &.product-comparison__compare-rows--visible {
        opacity: 1;
      }
    }
  }
</style>
